import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import DashboardSidebar from '../../components/ui/DashboardSidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import SubscriptionRequiredModal from '../../components/ui/SubscriptionRequiredModal';
import { useAuth } from '../../context/AuthContext';
import { fetchClassesInMySchool } from '../../lib/classroomsApi';
import { isSubscriptionError } from '../../lib/subscriptionGate';
import {
  approveCalendarInvite,
  addCalendarInvites,
  cancelCalendarEvent,
  createCalendarEvent,
  declineCalendarInvite,
  fetchCalendarEvents,
  searchCalendarUsers,
  type CalendarEventItem,
  type CalendarUserOption,
} from '../../lib/calendarApi';

type ClassroomOption = { id: number; name: string; is_member?: boolean; is_admin?: boolean };

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function ymdStart(d: Date): string {
  return `${ymd(d)}T00:00:00`;
}

function ymdEnd(d: Date): string {
  return `${ymd(d)}T23:59:59`;
}

function addDays(base: Date, days: number): Date {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  d.setDate(d.getDate() + days);
  return d;
}

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ro-RO', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const CalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const { me, uiRole, signOut } = useAuth();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const canCreate = uiRole === 'teacher' || uiRole === 'admin' || uiRole === 'secretariat' || uiRole === 'student' || uiRole === 'parent';
  const showSidebar = uiRole === 'teacher' || uiRole === 'admin' || uiRole === 'secretariat';
  const canInviteWide = uiRole === 'teacher' || uiRole === 'admin' || uiRole === 'secretariat';

  const user = useMemo(() => {
    const role = (uiRole || 'teacher') as any;
    return {
      role,
      name: me?.full_name || 'Utilizator',
      avatar: me?.avatar_url || undefined,
      is_platform_admin: Boolean((me as any)?.is_platform_admin),
    };
  }, [me?.avatar_url, me?.full_name, uiRole]);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  useEffect(() => {
    const handleResize = () => setIsSidebarCollapsed(window.innerWidth < 1024);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [monthCursor, setMonthCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedDay, setSelectedDay] = useState(() => ymd(new Date()));

  const [events, setEvents] = useState<CalendarEventItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyInviteEventId, setBusyInviteEventId] = useState<number | null>(null);
  const [expandedEventIds, setExpandedEventIds] = useState<Record<number, boolean>>({});

  const loadEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const start = ymdStart(new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1));
      const end = ymdEnd(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 0));
      const data = await fetchCalendarEvents({ start, end });
      setEvents(data);
    } catch (e: any) {
      setEvents([]);
      setError(e?.response?.data?.detail || 'Nu am putut incarca evenimentele.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthCursor]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEventItem[]>();
    for (const ev of events) {
      const day = (ev.start_time || '').slice(0, 10) || '—';
      const arr = map.get(day);
      if (arr) arr.push(ev);
      else map.set(day, [ev]);
    }
    for (const [k, v] of map.entries()) v.sort((a, b) => a.start_time.localeCompare(b.start_time));
    return map;
  }, [events]);

  const selectedEvents = useMemo(() => eventsByDay.get(selectedDay) || [], [eventsByDay, selectedDay]);

  const myId = Number(me?.id);
  const myParticipant = (ev: CalendarEventItem) =>
    Array.isArray(ev.participants) ? ev.participants.find((p) => Number(p.user_id) === myId) || null : null;

  const inviteLabel = (ev: CalendarEventItem): string | null => {
    const v = (ev as any)?.invite_label;
    const s = v == null ? '' : String(v).trim();
    return s ? s : null;
  };

  const createdByText = (ev: CalendarEventItem): string | null => {
    const c = (ev as any)?.created_by;
    const name = c?.full_name ? String(c.full_name).trim() : '';
    const email = c?.email ? String(c.email).trim() : '';
    if (name) return name;
    if (email) return email;
    return null;
  };

  const pendingInvites = useMemo(() => {
    const list: CalendarEventItem[] = [];
    for (const ev of events) {
      const mine = myParticipant(ev) as any;
      if (mine && String(mine.response || '').toLowerCase() === 'pending') list.push(ev);
    }
    list.sort((a, b) => a.start_time.localeCompare(b.start_time));
    return list;
  }, [events, myId]);

  const monthLabel = useMemo(
    () => monthCursor.toLocaleDateString('ro-RO', { month: 'long', year: 'numeric' }),
    [monthCursor]
  );

  const monthGrid = useMemo(() => {
    // Monday-first calendar grid
    const first = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1);
    const last = new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 0);
    const jsDay = first.getDay(); // 0 Sun
    const mondayIndex = (jsDay + 6) % 7; // 0 Mon
    const gridStart = addDays(first, -mondayIndex);
    const totalDays = 42; // 6 weeks grid
    const days = Array.from({ length: totalDays }, (_, i) => addDays(gridStart, i));
    return { first, last, days };
  }, [monthCursor]);

  const [classes, setClasses] = useState<ClassroomOption[]>([]);
  useEffect(() => {
    const load = async () => {
      if (!canInviteWide) return;
      try {
        const data = await fetchClassesInMySchool();
        setClasses(
          (Array.isArray(data) ? data : []).map((c: any) => ({
            id: Number(c.id),
            name: String(c.name || ''),
            is_member: Boolean(c.is_member),
            is_admin: Boolean(c.is_admin),
          }))
        );
      } catch {
        setClasses([]);
      }
    };
    void load();
  }, [canInviteWide]);

  const [openCreate, setOpenCreate] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createBusy, setCreateBusy] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    eventType: 'Sedinta',
    date: selectedDay,
    startTime: '08:00',
    endTime: '09:00',
    reminder: 30 as number | null,
    invitedClassroomId: '' as string,
    inviteParentsOfClass: false,
    inviteAllTeachers: false,
  });

  const [userQuery, setUserQuery] = useState('');
  const [userRole, setUserRole] = useState<string>('all');
  const [userResults, setUserResults] = useState<CalendarUserOption[]>([]);
  const [userSelected, setUserSelected] = useState<CalendarUserOption[]>([]);

  const [openInvite, setOpenInvite] = useState<{ eventId: number; title: string } | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    reminder: 30 as number | null,
    invitedClassroomId: '' as string,
    inviteParentsOfClass: false,
    inviteAllTeachers: false,
  });
  const [inviteUserQuery, setInviteUserQuery] = useState('');
  const [inviteUserRole, setInviteUserRole] = useState<string>('all');
  const [inviteUserResults, setInviteUserResults] = useState<CalendarUserOption[]>([]);
  const [inviteUserSelected, setInviteUserSelected] = useState<CalendarUserOption[]>([]);

  const userScopeLabel = (u: CalendarUserOption): string | null => {
    if (u.is_platform_admin) return 'platformă';
    if (u.school_name) return u.school_name;
    return null;
  };

  const eventTypeColor = (t?: string | null) => {
    const tt = (t || '').toString().trim().toLowerCase();
    if (tt === 'lucrare') return { border: 'border-error', bg: 'bg-error/10', dot: 'bg-error', pill: 'bg-error/15 text-error' };
    if (tt === 'sedinta') return { border: 'border-primary', bg: 'bg-primary/10', dot: 'bg-primary', pill: 'bg-primary/15 text-primary' };
    if (tt === 'programare') return { border: 'border-success', bg: 'bg-success/10', dot: 'bg-success', pill: 'bg-success/15 text-success' };
    return { border: 'border-border', bg: 'bg-muted/30', dot: 'bg-muted-foreground', pill: 'bg-muted text-foreground' };
  };

  const responseLabel = (resp?: string | null) => {
    const r = (resp || '').toString().trim().toLowerCase();
    if (r === 'accepted') return { text: 'Acceptat', cls: 'bg-success/15 text-success border-success/25' };
    if (r === 'declined') return { text: 'Respins', cls: 'bg-error/10 text-error border-error/20' };
    if (r === 'pending') return { text: 'În așteptare', cls: 'bg-warning/20 text-warning border-warning/30' };
    return { text: '—', cls: 'bg-muted text-muted-foreground border-border' };
  };

  const actInvite = async (eventId: number, action: 'approve' | 'decline') => {
    setBusyInviteEventId(eventId);
    try {
      if (action === 'approve') await approveCalendarInvite(eventId);
      else await declineCalendarInvite(eventId);
      await loadEvents();
    } finally {
      setBusyInviteEventId(null);
    }
  };

  const searchUsers = async () => {
    const q = userQuery.trim();
    if (!q && userRole !== 'admin') return setUserResults([]);
    try {
      const data = await searchCalendarUsers({
        q,
        role: userRole === 'all' ? null : userRole,
        limit: userRole === 'admin' ? 50 : 20,
      });
      setUserResults(data);
    } catch {
      setUserResults([]);
    }
  };

  useEffect(() => {
    const t = window.setTimeout(() => void searchUsers(), 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userQuery, userRole]);

  const searchInviteUsers = async () => {
    const q = inviteUserQuery.trim();
    if (!q && inviteUserRole !== 'admin') return setInviteUserResults([]);
    try {
      const data = await searchCalendarUsers({
        q,
        role: inviteUserRole === 'all' ? null : inviteUserRole,
        limit: inviteUserRole === 'admin' ? 50 : 20,
      });
      setInviteUserResults(data);
    } catch {
      setInviteUserResults([]);
    }
  };

  useEffect(() => {
    const t = window.setTimeout(() => void searchInviteUsers(), 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteUserQuery, inviteUserRole]);

  const addSelectedUser = (u: CalendarUserOption) => {
    if (userSelected.some((x) => Number(x.id) === Number(u.id))) return;
    setUserSelected((prev) => [...prev, u]);
  };

  const removeSelectedUser = (id: number) => setUserSelected((prev) => prev.filter((x) => Number(x.id) !== Number(id)));

  const addInviteSelectedUser = (u: CalendarUserOption) => {
    if (inviteUserSelected.some((x) => Number(x.id) === Number(u.id))) return;
    setInviteUserSelected((prev) => [...prev, u]);
  };

  const removeInviteSelectedUser = (id: number) => setInviteUserSelected((prev) => prev.filter((x) => Number(x.id) !== Number(id)));

  const resetCreate = () => {
    setCreateError(null);
    setUserQuery('');
    setUserRole('all');
    setUserResults([]);
    setUserSelected([]);
    setCreateForm({
      title: '',
      description: '',
      eventType: 'Sedinta',
      date: selectedDay,
      startTime: '08:00',
      endTime: '09:00',
      reminder: 30,
      invitedClassroomId: '',
      inviteParentsOfClass: false,
      inviteAllTeachers: false,
    });
  };

  const resetInvite = () => {
    setInviteError(null);
    setInviteUserQuery('');
    setInviteUserRole('all');
    setInviteUserResults([]);
    setInviteUserSelected([]);
    setInviteForm({
      reminder: 30,
      invitedClassroomId: '',
      inviteParentsOfClass: false,
      inviteAllTeachers: false,
    });
  };

  const submitCreate = async () => {
    setCreateError(null);
    const title = createForm.title.trim();
    if (!title) return setCreateError('Completeaza titlul.');
    const date = (createForm.date || '').trim();
    const startTime = (createForm.startTime || '').trim();
    const endTime = (createForm.endTime || '').trim();
    if (!date) return setCreateError('Alege o data.');
    if (!/^\d{2}:\d{2}$/.test(startTime) || !/^\d{2}:\d{2}$/.test(endTime)) return setCreateError('Completeaza orele (HH:MM).');
    const startIso = `${date}T${startTime}:00`;
    const endIso = `${date}T${endTime}:00`;
    if (new Date(endIso).getTime() <= new Date(startIso).getTime()) return setCreateError('Interval invalid (end trebuie sa fie dupa start).');

    setCreateBusy(true);
    try {
      await createCalendarEvent({
        title,
        description: createForm.description.trim() || null,
        event_type: createForm.eventType,
        start_time: startIso,
        end_time: endIso,
        reminder_minutes: createForm.reminder,
        invited_classroom_id:
          canInviteWide && createForm.invitedClassroomId ? Number(createForm.invitedClassroomId) : null,
        invite_parents_of_class: canInviteWide ? Boolean(createForm.inviteParentsOfClass) : false,
        invite_all_teachers: canInviteWide ? Boolean(createForm.inviteAllTeachers) : false,
        invited_user_ids: userSelected.length ? userSelected.map((u) => u.id) : null,
      });
      setOpenCreate(false);
      resetCreate();
      await loadEvents();
    } catch (e: any) {
      if (isSubscriptionError(e)) {
        setUpgradeOpen(true);
        setCreateError(null);
        return;
      }
      setCreateError(e?.response?.data?.detail || 'Nu am putut crea evenimentul.');
    } finally {
      setCreateBusy(false);
    }
  };

  const submitInvite = async () => {
    if (!openInvite) return;
    setInviteError(null);
    setInviteBusy(true);
    try {
      const payload: any = {
        reminder_minutes: inviteForm.reminder,
        invited_user_ids: inviteUserSelected.map((u) => Number(u.id)),
      };

      if (canInviteWide && inviteForm.invitedClassroomId) payload.invited_classroom_id = Number(inviteForm.invitedClassroomId);
      if (canInviteWide) payload.invite_parents_of_class = Boolean(inviteForm.inviteParentsOfClass);
      if (canInviteWide) payload.invite_all_teachers = Boolean(inviteForm.inviteAllTeachers);

      await addCalendarInvites(openInvite.eventId, payload);
      await loadEvents();
      setOpenInvite(null);
      resetInvite();
    } catch (e: any) {
      if (isSubscriptionError(e)) {
        setUpgradeOpen(true);
        setInviteError(null);
        return;
      }
      setInviteError(e?.response?.data?.detail || 'Nu am putut adauga invitatii.');
    } finally {
      setInviteBusy(false);
    }
  };

  const handleNavigation = (path: string) => navigate(path);

  return (
    <>
      <Helmet>
        <title>Calendar - Scola</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header user={user} onNavigate={handleNavigation} onSignOut={() => signOut()} />
        {showSidebar && <DashboardSidebar user={user} isCollapsed={isSidebarCollapsed} onNavigate={handleNavigation} />}

        <main
          className={`pt-16 transition-all duration-300 ${
            showSidebar ? (isSidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64') : ''
          }`}
        >
          <div className="p-4 lg:p-6 max-w-[1600px] mx-auto">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Calendar</h1>
                <p className="text-sm text-muted-foreground">Sedinte, lucrari, programari si reminder.</p>
              </div>
              {canCreate && (
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => {
                    resetCreate();
                    setOpenCreate(true);
                  }}
                >
                  Programeaza
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              <div className="lg:col-span-2 bg-card border border-border rounded-lg p-4 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <Icon name="CalendarDays" size={18} className="text-muted-foreground" />
                    <div className="text-sm font-semibold text-foreground capitalize">{monthLabel}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 justify-start sm:justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="ChevronLeft"
                      onClick={() => setMonthCursor((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
                    >
                      <span className="sm:hidden">Anterior</span>
                      <span className="hidden sm:inline">Luna anterioara</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Calendar"
                      onClick={() => {
                        const d = new Date();
                        setMonthCursor(new Date(d.getFullYear(), d.getMonth(), 1));
                        setSelectedDay(ymd(d));
                      }}
                    >
                      Azi
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="ChevronRight"
                      onClick={() => setMonthCursor((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
                    >
                      <span className="sm:hidden">Următoare</span>
                      <span className="hidden sm:inline">Luna urmatoare</span>
                    </Button>
                    <Button variant="outline" size="sm" iconName="RefreshCw" onClick={() => void loadEvents()} disabled={loading}>
                      <span className="sm:hidden">Refresh</span>
                      <span className="hidden sm:inline">Reincarca</span>
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-7 gap-2 text-xs text-muted-foreground mb-2">
                  {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d, idx) => (
                    <div key={`${d}-${idx}`} className="px-2">
                      {d}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-2">
                  {monthGrid.days.map((d) => {
                    const key = ymd(d);
                    const isInMonth = d.getMonth() === monthCursor.getMonth();
                    const isSelected = key === selectedDay;
                    const isToday = key === ymd(new Date());
                    const count = (eventsByDay.get(key) || []).length;
                    const dayEvents = eventsByDay.get(key) || [];
                    const dots = Array.from(
                      new Set(dayEvents.map((e) => (e.event_type || 'Altul').toString().trim().toLowerCase()))
                    )
                      .slice(0, 3)
                      .map((t) => eventTypeColor(t).dot);
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setSelectedDay(key)}
                        className={`relative rounded-md border px-2 py-2 text-left transition-colors duration-150 ${
                          isSelected
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:bg-muted/40'
                        } ${isInMonth ? 'text-foreground' : 'text-muted-foreground'}`}
                        aria-current={isSelected ? 'date' : undefined}
                      >
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-medium ${isToday ? 'text-primary' : ''}`}>{d.getDate()}</span>
                          {count > 0 && (
                            <span className="text-[11px] px-1.5 py-0.5 rounded bg-muted text-foreground">{count}</span>
                          )}
                        </div>
                        {dots.length > 0 && (
                          <div className="mt-2 flex gap-1">
                            {dots.map((cls, idx) => (
                              <span key={`${key}-dot-${idx}`} className={`w-2 h-2 rounded-full ${cls}`} />
                            ))}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold text-foreground">Programari</div>
                  <div className="text-xs text-muted-foreground">{selectedDay}</div>
                </div>
                {pendingInvites.length > 0 && (
                  <div className="mb-4 border border-warning/30 bg-warning/10 rounded-md p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-medium text-foreground">Invitatii in asteptare</div>
                      <div className="text-xs text-muted-foreground">{pendingInvites.length}</div>
                    </div>
                    <div className="mt-2 space-y-2">
                      {pendingInvites.slice(0, 4).map((ev) => (
                        <div key={`pending-${ev.id}`} className="bg-background border border-border rounded-md p-2">
                          <div className="text-xs text-muted-foreground">{fmtDateTime(ev.start_time)}</div>
                          <div className="text-sm font-medium text-foreground truncate">{ev.title}</div>
                          {createdByText(ev) && (
                            <div className="text-xs text-muted-foreground mt-1">De la: {createdByText(ev)}</div>
                          )}
                          {inviteLabel(ev) && <div className="text-xs text-muted-foreground mt-1">{inviteLabel(ev)}</div>}
                          <div className="mt-2 flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="xs"
                              onClick={() => void actInvite(ev.id, 'decline')}
                              disabled={busyInviteEventId === ev.id}
                            >
                              Respinge
                            </Button>
                            <Button
                              variant="default"
                              size="xs"
                              onClick={() => void actInvite(ev.id, 'approve')}
                              disabled={busyInviteEventId === ev.id}
                            >
                              Accepta
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                    {pendingInvites.length > 4 && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        +{pendingInvites.length - 4} invitatii...
                      </div>
                    )}
                  </div>
                )}
                {loading ? (
                  <div className="text-sm text-muted-foreground">Se incarca...</div>
                ) : selectedEvents.length === 0 ? (
                  <div className="text-sm text-muted-foreground">Nu exista programari in aceasta zi.</div>
                ) : (
                  <div className="space-y-2">
                    {selectedEvents.map((ev) => {
                      const colors = eventTypeColor(ev.event_type);
                      const mine = myParticipant(ev);
                      const resp = responseLabel((mine as any)?.response ?? null);
                      const expanded = Boolean(expandedEventIds[ev.id]);
                      const ilabel = inviteLabel(ev);
                      const createdBy = createdByText(ev);
                      const canManage = Number(ev.created_by_id) === Number(myId) || uiRole === 'teacher' || uiRole === 'admin';

                      const participants = Array.isArray(ev.participants) ? ev.participants : [];
                      const byResp = participants.reduce(
                        (acc, p: any) => {
                          const r = (p?.response || '').toString().toLowerCase();
                          if (r === 'declined') acc.declined.push(p);
                          else if (r === 'pending') acc.pending.push(p);
                          else acc.accepted.push(p);
                          return acc;
                        },
                        { accepted: [] as any[], pending: [] as any[], declined: [] as any[] }
                      );

                      return (
                        <div key={ev.id} className={`border rounded-md p-3 bg-background ${colors.border}`}>
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="text-sm font-medium text-foreground truncate">{ev.title}</div>
                                <span className={`text-xs px-2 py-0.5 rounded border ${colors.pill}`}>{ev.event_type || 'Altul'}</span>
                                {mine && (
                                  <span className={`text-xs px-2 py-0.5 rounded border ${resp.cls}`}>{resp.text}</span>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                {fmtDateTime(ev.start_time)} → {fmtDateTime(ev.end_time)}
                              </div>
                              {createdBy && <div className="text-xs text-muted-foreground mt-1">De la: {createdBy}</div>}
                              {ilabel && <div className="text-xs text-muted-foreground mt-1">{ilabel}</div>}
                              {ev.description && (
                                <div className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap break-words">
                                  {ev.description}
                                </div>
                              )}

                              {mine && (mine as any)?.response === 'pending' && (
                                <div className="mt-3 flex items-center gap-2 flex-wrap">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => void actInvite(ev.id, 'decline')}
                                    disabled={busyInviteEventId === ev.id}
                                  >
                                    Respinge
                                  </Button>
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => void actInvite(ev.id, 'approve')}
                                    disabled={busyInviteEventId === ev.id}
                                  >
                                    Accepta
                                  </Button>
                                </div>
                              )}
                            </div>

                            <div className="shrink-0 flex flex-row sm:flex-col sm:items-end items-center gap-2 flex-wrap">
                              {String(ev.event_type || '').toLowerCase().includes('online') && (
                                <Button
                                  variant="default"
                                  size="xs"
                                  onClick={() => navigate(`/online-classroom?event=${ev.id}`)}
                                  title="Intră în clasa online"
                                >
                                  Intră
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="xs"
                                onClick={() => setExpandedEventIds((p) => ({ ...p, [ev.id]: !Boolean(p[ev.id]) }))}
                                title="Vezi invitatii"
                              >
                                <span className="flex items-center gap-2">
                                  <span>Invitati</span>
                                  <span className="px-2 py-0.5 rounded border border-border bg-muted/40 text-foreground">
                                    {participants.length}
                                  </span>
                                </span>
                              </Button>
                              {canManage && (
                                <Button
                                  variant="outline"
                                  size="xs"
                                  onClick={() => {
                                    resetInvite();
                                    setOpenInvite({ eventId: ev.id, title: ev.title });
                                  }}
                                >
                                  Adauga invitati
                                </Button>
                              )}
                              {canManage && (
                                <Button
                                  variant="outline"
                                  size="xs"
                                  onClick={async () => {
                                    const ok = window.confirm('Anulezi aceasta programare?');
                                    if (!ok) return;
                                    await cancelCalendarEvent(ev.id);
                                    await loadEvents();
                                  }}
                                >
                                  Anuleaza
                                </Button>
                              )}
                            </div>
                          </div>

                          {expanded && (
                            <div className="mt-3 border-t border-border pt-3 space-y-3">
                              <div className="flex flex-wrap gap-2 text-xs">
                                <span className="px-2 py-0.5 rounded border bg-success/10 text-success border-success/20">
                                  Acceptati: {byResp.accepted.length}
                                </span>
                                <span className="px-2 py-0.5 rounded border bg-warning/15 text-warning border-warning/25">
                                  In asteptare: {byResp.pending.length}
                                </span>
                                <span className="px-2 py-0.5 rounded border bg-error/10 text-error border-error/20">
                                  Respinsi: {byResp.declined.length}
                                </span>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {[
                                  { title: 'Acceptat', items: byResp.accepted, cls: 'text-success' },
                                  { title: 'În așteptare', items: byResp.pending, cls: 'text-warning' },
                                  { title: 'Respins', items: byResp.declined, cls: 'text-error' },
                                ].map((g) => (
                                  <div key={g.title} className="bg-muted/20 border border-border rounded-md p-3">
                                    <div className={`text-xs font-semibold mb-2 ${g.cls}`}>{g.title}</div>
                                    {g.items.length === 0 ? (
                                      <div className="text-xs text-muted-foreground">—</div>
                                    ) : (
                                      <div className="space-y-1">
                                        {g.items.slice(0, 12).map((p: any) => (
                                          <div key={`${ev.id}-${p.user_id}-${g.title}`} className="text-xs text-foreground">
                                            {(p.full_name || p.email || `#${p.user_id}`) as string}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="mt-3 text-xs text-muted-foreground">
                  Selecteaza o data din calendar si programeaza un interval orar.
                </div>
              </div>
            </div>

            {error && <div className="mb-4 bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{error}</div>}
          </div>
        </main>

        {openCreate && (
          <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
            <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-scale-in">
              <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
                <h3 className="text-lg font-semibold text-foreground">Programeaza</h3>
                <button className="p-2 rounded-md hover:bg-muted transition-colors duration-150" onClick={() => setOpenCreate(false)} aria-label="Inchide">
                  <Icon name="X" size={18} className="text-muted-foreground" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                {createError && <div className="bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{createError}</div>}

                <Input label="Titlu *" value={createForm.title} onChange={(e) => setCreateForm((p) => ({ ...p, title: e.target.value }))} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-medium text-foreground mb-2">Tip</div>
                    <select
                      className="w-full h-10 px-3 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      value={createForm.eventType}
                      onChange={(e) => setCreateForm((p) => ({ ...p, eventType: e.target.value }))}
                    >
                      {['Clasă online', 'Sedinta', 'Lucrare', 'Programare', 'Altul'].map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground mb-2">Reminder</div>
                    <select
                      className="w-full h-10 px-3 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      value={createForm.reminder == null ? '' : String(createForm.reminder)}
                      onChange={(e) => setCreateForm((p) => ({ ...p, reminder: e.target.value ? Number(e.target.value) : null }))}
                    >
                      <option value="">Fara reminder</option>
                      <option value="5">Cu 5 minute inainte</option>
                      <option value="10">Cu 10 minute inainte</option>
                      <option value="30">Cu 30 minute inainte</option>
                      <option value="60">Cu 1 ora inainte</option>
                      <option value="1440">Cu 1 zi inainte</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Data *"
                    type="date"
                    value={createForm.date}
                    onChange={(e) => setCreateForm((p) => ({ ...p, date: e.target.value }))}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Start *"
                      type="time"
                      value={createForm.startTime}
                      onChange={(e) => setCreateForm((p) => ({ ...p, startTime: e.target.value }))}
                    />
                    <Input
                      label="End *"
                      type="time"
                      value={createForm.endTime}
                      onChange={(e) => setCreateForm((p) => ({ ...p, endTime: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium text-foreground mb-2">Descriere</div>
                  <textarea
                    className="w-full min-h-[90px] px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    value={createForm.description}
                    onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))}
                    placeholder="Detalii (optional)"
                  />
                </div>

                <div className="border-t border-border pt-4 space-y-3">
                  <div className="text-sm font-medium text-foreground">Invitatii</div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-foreground mb-2">Clasa (optional)</div>
                      <select
                        className="w-full h-10 px-3 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        value={createForm.invitedClassroomId}
                        onChange={(e) => setCreateForm((p) => ({ ...p, invitedClassroomId: e.target.value }))}
                        disabled={!canInviteWide}
                      >
                        <option value="">—</option>
                        {classes.map((c) => (
                          <option key={c.id} value={String(c.id)}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      <div className="mt-2 space-y-2">
                        <label className="flex items-center gap-2 text-sm text-foreground">
                          <input
                            type="checkbox"
                            checked={createForm.inviteParentsOfClass}
                            onChange={(e) => setCreateForm((p) => ({ ...p, inviteParentsOfClass: e.target.checked }))}
                            className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                            disabled={!canInviteWide || !createForm.invitedClassroomId}
                          />
                          <span>Invita parintii clasei</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-foreground">
                          <input
                            type="checkbox"
                            checked={createForm.inviteAllTeachers}
                            onChange={(e) => setCreateForm((p) => ({ ...p, inviteAllTeachers: e.target.checked }))}
                            className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                            disabled={!canInviteWide}
                          />
                          <span>Invita toti profesorii scolii</span>
                        </label>
                        {!canInviteWide && (
                          <div className="text-xs text-muted-foreground">
                            Ca părinte/elev poți invita persoane individuale (nu clase sau liste extinse).
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <div className="text-sm font-medium text-foreground mb-2">Adauga persoane (optional)</div>
                      <div className="flex gap-2">
                        <input
                          className="flex-1 h-10 px-3 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                          value={userQuery}
                          onChange={(e) => setUserQuery(e.target.value)}
                          placeholder="Cauta nume/email..."
                        />
                        <select
                          className="h-10 px-3 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                          value={userRole}
                          onChange={(e) => setUserRole(e.target.value)}
                        >
                          <option value="all">Toate</option>
                          <option value="admin">Admini</option>
                          <option value="profesor">Profesori</option>
                          <option value="elev">Elevi</option>
                          <option value="parinte">Parinti</option>
                        </select>
                      </div>

                      {userResults.length > 0 && (
                        <div className="mt-2 max-h-40 overflow-y-auto border border-border rounded-md divide-y divide-border">
                          {userResults.map((u) => (
                            <button
                              key={u.id}
                              type="button"
                              onClick={() => addSelectedUser(u)}
                              className="w-full px-3 py-2 text-left hover:bg-muted transition-colors duration-150"
                            >
                              <div className="text-sm text-foreground">{u.full_name}</div>
                              <div className="text-xs text-muted-foreground">
                                {u.email} • {u.role}
                                {userScopeLabel(u) ? ` • ${userScopeLabel(u)}` : ''}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {userSelected.length > 0 && (
                        <div className="mt-3 space-y-2">
                          <div className="text-xs text-muted-foreground">Selectati</div>
                          <div className="flex flex-wrap gap-2">
                            {userSelected.map((u) => (
                              <span key={u.id} className="inline-flex items-center gap-2 px-2 py-1 rounded border border-border bg-muted text-xs text-foreground">
                                <span>{u.full_name}</span>
                                <button type="button" onClick={() => removeSelectedUser(u.id)} className="text-muted-foreground hover:text-foreground">
                                  <Icon name="X" size={14} />
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setOpenCreate(false)}>
                    Anuleaza
                  </Button>
                  <Button variant="default" onClick={submitCreate} disabled={createBusy}>
                    {createBusy ? 'Se salveaza...' : 'Salveaza'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {openInvite && (
          <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
            <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-scale-in">
              <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
                <h3 className="text-lg font-semibold text-foreground">Adauga invitati</h3>
                <button className="p-2 rounded-md hover:bg-muted transition-colors duration-150" onClick={() => setOpenInvite(null)} aria-label="Inchide">
                  <Icon name="X" size={18} className="text-muted-foreground" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <div className="text-sm text-muted-foreground">Eveniment: <span className="text-foreground font-medium">{openInvite.title}</span></div>
                {inviteError && <div className="bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{inviteError}</div>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-medium text-foreground mb-2">Reminder</div>
                    <select
                      className="w-full h-10 px-3 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      value={inviteForm.reminder == null ? '' : String(inviteForm.reminder)}
                      onChange={(e) => setInviteForm((p) => ({ ...p, reminder: e.target.value ? Number(e.target.value) : null }))}
                    >
                      <option value="">Fara reminder</option>
                      <option value="5">Cu 5 minute inainte</option>
                      <option value="10">Cu 10 minute inainte</option>
                      <option value="30">Cu 30 minute inainte</option>
                      <option value="60">Cu 1 ora inainte</option>
                      <option value="1440">Cu 1 zi inainte</option>
                    </select>
                  </div>
                </div>

                <div className="border-t border-border pt-4 space-y-3">
                  <div className="text-sm font-medium text-foreground">Invitatii</div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-foreground mb-2">Cauta utilizator</div>
                      <div className="flex gap-2">
                        <Input
                          value={inviteUserQuery}
                          onChange={(e) => setInviteUserQuery(e.target.value)}
                          placeholder="Nume / email / username"
                        />
                        <select
                          className="h-10 px-2 text-sm border border-border rounded-md bg-background text-foreground"
                          value={inviteUserRole}
                          onChange={(e) => setInviteUserRole(e.target.value)}
                        >
                          <option value="all">Toate</option>
                          <option value="admin">Admini</option>
                          <option value="profesor">Profesori</option>
                          <option value="elev">Elevi</option>
                          <option value="parinte">Parinti</option>
                        </select>
                      </div>
                      {inviteUserResults.length > 0 && (
                        <div className="mt-2 border border-border rounded-md overflow-hidden max-h-48 overflow-y-auto">
                          {inviteUserResults.slice(0, 20).map((u) => (
                            <button
                              key={`inv-${u.id}`}
                              type="button"
                              className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex items-center justify-between gap-2"
                              onClick={() => addInviteSelectedUser(u)}
                            >
                              <span className="truncate">
                                {u.full_name} <span className="text-xs text-muted-foreground">({u.role})</span>
                                {userScopeLabel(u) ? <span className="text-xs text-muted-foreground"> • {userScopeLabel(u)}</span> : null}
                              </span>
                              <span className="text-xs text-muted-foreground truncate">{u.email}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="text-sm font-medium text-foreground mb-2">Selectati</div>
                      {inviteUserSelected.length === 0 ? (
                        <div className="text-sm text-muted-foreground">Nu ai selectat invitati.</div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {inviteUserSelected.map((u) => (
                            <button
                              key={`sel-${u.id}`}
                              type="button"
                              className="text-xs px-2 py-1 rounded border border-border hover:bg-muted"
                              onClick={() => removeInviteSelectedUser(Number(u.id))}
                              title="Elimina"
                            >
                              {u.full_name || u.email}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    <div>
                      <div className="text-sm font-medium text-foreground mb-2">Clasa (optional)</div>
                      <select
                        className="w-full h-10 px-3 text-sm border border-border rounded-md bg-background text-foreground disabled:opacity-60"
                        value={inviteForm.invitedClassroomId}
                        onChange={(e) => setInviteForm((p) => ({ ...p, invitedClassroomId: e.target.value }))}
                        disabled={!canInviteWide}
                      >
                        <option value="">—</option>
                        {classes.map((c) => (
                          <option key={`c-${c.id}`} value={String(c.id)}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2 pt-1">
                      <label className="flex items-center gap-2 text-sm text-foreground">
                        <input
                          type="checkbox"
                          checked={inviteForm.inviteParentsOfClass}
                          onChange={(e) => setInviteForm((p) => ({ ...p, inviteParentsOfClass: e.target.checked }))}
                          disabled={!canInviteWide || !inviteForm.invitedClassroomId}
                        />
                        Invita parintii clasei
                      </label>
                      <label className="flex items-center gap-2 text-sm text-foreground">
                        <input
                          type="checkbox"
                          checked={inviteForm.inviteAllTeachers}
                          onChange={(e) => setInviteForm((p) => ({ ...p, inviteAllTeachers: e.target.checked }))}
                          disabled={!canInviteWide}
                        />
                        Invita toti profesorii scolii
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setOpenInvite(null)} disabled={inviteBusy}>
                    Inchide
                  </Button>
                  <Button variant="default" onClick={() => void submitInvite()} disabled={inviteBusy}>
                    {inviteBusy ? 'Se salveaza...' : 'Adauga'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <SubscriptionRequiredModal open={upgradeOpen} feature="calendar" onClose={() => setUpgradeOpen(false)} />
      </div>
    </>
  );
};

export default CalendarPage;
